import { ParentSize } from '@visx/responsive'
import { useRef, useMemo, useState } from 'react'
import Spinner from '../Spinner'
import GraphHeader from '../viz/GraphHeader'
import { FunnelChart, FunnelSection } from '../viz/graphs/FunnelChart'
import SegmentControl from '../viz/SegmentControl'
import { ConversionType } from 'app/types/entities'
import type { ConversionMetricsResponse } from 'types/analytics'

const STAGE_LABELS: Record<ConversionType, { label: string; altLabel: string; tooltip: string }> = {
  QUALIFIED_LEAD_GENERATED: {
    label: `Qualified Leads`,
    altLabel: `Lead → Qualified Lead`,
    tooltip: `Conversions sent when a contact becomes a qualified lead.`,
  },
  NEW_PATIENT_APPOINTMENT_SCHEDULED: {
    label: `Appointments Scheduled`,
    altLabel: `Qualified → Appointment`,
    tooltip: `Conversions sent when a contact schedules their first appointment.`,
  },
  NEW_PATIENT_GENERATED: {
    label: `New Patients`,
    altLabel: `Appointment → Patient`,
    tooltip: `Conversions sent when a contact becomes a patient.`,
  },
  ADDITIONAL_TREATMENT_REVENUE: {
    label: `Additional Revenue`,
    altLabel: `Patient → Revenue`,
    tooltip: `Conversions sent for additional treatment revenue beyond FVV.`,
  },
}

const CONVERSION_TYPES_ORDER: ConversionType[] = [
  `QUALIFIED_LEAD_GENERATED`,
  `NEW_PATIENT_APPOINTMENT_SCHEDULED`,
  `NEW_PATIENT_GENERATED`,
  `ADDITIONAL_TREATMENT_REVENUE`,
]

type Props = {
  metrics?: ConversionMetricsResponse
  isLoading: boolean
  onStageClick: (conversionType: ConversionType) => void
}

export default function ConversionFunnelChart({ metrics, isLoading, onStageClick }: Props) {
  const graphContainerRef = useRef<HTMLDivElement>(null)
  const [percentageView, setPercentageView] = useState(false)

  const funnelSections: FunnelSection[] = useMemo(() => {
    if (!metrics?.stages) {
      return CONVERSION_TYPES_ORDER.map((type) => ({
        label: STAGE_LABELS[type].label,
        altLabel: STAGE_LABELS[type].altLabel,
        value: 0,
        onDetailClick: () => onStageClick(type),
        infoTooltip: { title: STAGE_LABELS[type].label, message: STAGE_LABELS[type].tooltip },
      }))
    }

    return CONVERSION_TYPES_ORDER.map((type) => {
      const stage = metrics.stages[type]
      return {
        label: STAGE_LABELS[type].label,
        subLabel: stage.failed > 0 ? `${stage.failed} failed` : undefined,
        altLabel: STAGE_LABELS[type].altLabel,
        value: stage.total,
        onDetailClick: () => onStageClick(type),
        infoTooltip: { title: STAGE_LABELS[type].label, message: STAGE_LABELS[type].tooltip },
      }
    })
  }, [metrics, onStageClick])

  return (
    <div>
      <GraphHeader
        leftSection={[{ title: `Conversion Funnel by Stage`, count: null }]}
        rightSection={[
          <SegmentControl
            key="view-toggle"
            label="View:"
            buttons={[
              { label: `Total`, onClick: () => setPercentageView(false), isActive: !percentageView },
              { label: `Conversion`, onClick: () => setPercentageView(true), isActive: percentageView },
            ]}
          />,
        ]}
      />
      <div ref={graphContainerRef} className="h-[345px]">
        {!isLoading ? (
          <ParentSize>
            {() => (
              <FunnelChart
                width={graphContainerRef.current?.clientWidth || 800}
                height={(graphContainerRef.current?.clientHeight || 345) / 2}
                sections={funnelSections}
                percentageView={percentageView}
                isLoading={isLoading}
                // Opacity steps on one shade rather than a 100->800 shade ramp: shades 700-950
                // lighten and 50-300 darken in dark mode (see dark-hues.css), which would
                // reverse the deepening order.
                colorScheme={{
                  base: `text-teal-600/15`,
                  segments: [`text-teal-600/35`, `text-teal-600/55`, `text-teal-600/75`, `text-teal-600`],
                }}
              />
            )}
          </ParentSize>
        ) : (
          <div className="grid h-full place-items-center">
            <Spinner size={20} />
          </div>
        )}
      </div>
    </div>
  )
}
